import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase_client';
import '../styles/announcement-banner.css';

export interface Announcement {
  id: string;
  title: string;
  summary: string;
  detail: string | null;
  severity: 'info' | 'warning' | 'urgent';
  country_iso: string | null;
  placement: string;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  display_order: number;
  created_at?: string;
  updated_at?: string;
}

const dismissKey = (id: string) => `announcement_dismissed_${id}`;

const hasHtmlContent = (html: string | null | undefined) =>
  !!html && !!html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();

/** Renders announcement HTML (bold/lists/links) safely for banner + detail modal.
 *  Plain-text legacy rows keep newline formatting.
 */
function AnnouncementHtml({
  html,
  className = '',
  inline = false,
}: {
  html: string;
  className?: string;
  inline?: boolean;
}) {
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(html);

  if (!looksLikeHtml) {
    return (
      <span className={`${inline ? '' : 'whitespace-pre-wrap'} ${className}`.trim()}>
        {html}
      </span>
    );
  }

  return (
    <span
      className={`announcement-html ${inline ? 'announcement-html--inline' : ''} ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

const severityStyles: Record<
  Announcement['severity'],
  { banner: string; button: string; iconBg: string; iconText: string; detailBox: string; detailTitle: string; detailBody: string }
> = {
  info: {
    banner: 'bg-blue-600',
    button: 'bg-blue-700 hover:bg-blue-800',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    detailBox: 'bg-blue-50 border-blue-200',
    detailTitle: 'text-blue-800',
    detailBody: 'text-blue-700',
  },
  warning: {
    banner: 'bg-amber-500',
    button: 'bg-amber-600 hover:bg-amber-700',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    detailBox: 'bg-amber-50 border-amber-200',
    detailTitle: 'text-amber-800',
    detailBody: 'text-amber-700',
  },
  urgent: {
    banner: 'bg-red-600',
    button: 'bg-red-700 hover:bg-red-800',
    iconBg: 'bg-red-100',
    iconText: 'text-red-600',
    detailBox: 'bg-red-50 border-red-200',
    detailTitle: 'text-red-800',
    detailBody: 'text-red-700',
  },
};

function SeverityIcon({ severity, className }: { severity: Announcement['severity']; className?: string }) {
  if (severity === 'urgent') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    );
  }
  if (severity === 'warning') {
    return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    );
  }
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

type Props = {
  placement?: string;
};

export const AnnouncementsBanner: React.FC<Props> = ({ placement = 'all_filing' }) => {
  const { activeOrg } = useSession();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [detailId, setDetailId] = useState<string | null>(null);

  const fetchAnnouncements = useCallback(async () => {
    const countryIso = activeOrg?.countryIso;
    if (!countryIso) {
      setAnnouncements([]);
      return;
    }

    const { data, error } = await supabase
      .from('announcements')
      .select('id, title, summary, detail, severity, country_iso, placement, is_active, starts_at, ends_at, display_order')
      .eq('placement', placement)
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncements([]);
      return;
    }

    const now = Date.now();
    const visible = (data || []).filter((row: Announcement) => {
      if (row.country_iso && row.country_iso !== countryIso) return false;
      if (row.starts_at && new Date(row.starts_at).getTime() > now) return false;
      if (row.ends_at && new Date(row.ends_at).getTime() < now) return false;
      if (localStorage.getItem(dismissKey(row.id))) return false;
      return true;
    });

    setAnnouncements(visible);
  }, [activeOrg?.countryIso, placement]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleDismiss = (id: string) => {
    localStorage.setItem(dismissKey(id), 'true');
    setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    if (detailId === id) setDetailId(null);
  };

  if (announcements.length === 0) {
    return null;
  }

  const detailAnnouncement = detailId
    ? announcements.find((a) => a.id === detailId) ?? null
    : null;

  return (
    <div style={{ marginTop: 0 }}>
      {announcements.map((announcement) => {
        const styles = severityStyles[announcement.severity] || severityStyles.info;
        return (
          <div
            key={announcement.id}
            className={`w-full ${styles.banner} text-white py-3 px-4 shadow-md mb-4 rounded-lg`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <SeverityIcon severity={announcement.severity} className="w-6 h-6 flex-shrink-0" />
                <div className="text-sm font-medium flex-1 min-w-0">
                  <strong>{announcement.title}:</strong>{' '}
                  <AnnouncementHtml html={announcement.summary} inline />
                  {hasHtmlContent(announcement.detail) ? (
                    <button
                      type="button"
                      onClick={() => setDetailId(announcement.id)}
                      className="ml-2 underline hover:no-underline font-semibold whitespace-nowrap"
                    >
                      See details
                    </button>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDismiss(announcement.id)}
                className={`ml-2 flex-shrink-0 ${styles.button} text-white px-4 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap`}
                aria-label={`Dismiss ${announcement.title}`}
              >
                Got it
              </button>
            </div>
          </div>
        );
      })}

      {detailAnnouncement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
            <div className="flex items-start justify-between px-6 py-5 border-b">
              <div className="flex items-center space-x-3">
                <div
                  className={`flex-shrink-0 w-10 h-10 ${severityStyles[detailAnnouncement.severity].iconBg} rounded-full flex items-center justify-center`}
                >
                  <SeverityIcon
                    severity={detailAnnouncement.severity}
                    className={`w-5 h-5 ${severityStyles[detailAnnouncement.severity].iconText}`}
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{detailAnnouncement.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5 capitalize">{detailAnnouncement.severity} notice</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailId(null)}
                className="text-gray-400 hover:text-gray-600 ml-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5">
              <div
                className={`${severityStyles[detailAnnouncement.severity].detailBox} border rounded-lg p-4 text-sm text-gray-800`}
              >
                <AnnouncementHtml html={detailAnnouncement.detail || ''} />
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-between items-center">
              <button
                type="button"
                onClick={() => handleDismiss(detailAnnouncement.id)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Don't show again
              </button>
              <button
                type="button"
                onClick={() => setDetailId(null)}
                className="f-btn f-btn-primary"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsBanner;
